import { useCallback, useEffect, useState } from 'react';

import { getThemeChoice, setThemeChoice, type ThemeChoice } from '@/lib/storage';

const query = (): MediaQueryList | null =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function' ? null : window.matchMedia('(prefers-color-scheme: dark)');

type Resolved = 'light' | 'dark';

const resolve = (choice: string | undefined, system: string | undefined): Resolved | null =>
  choice === 'light' || choice === 'dark' ? choice : choice === 'auto' ? (system === 'dark' ? 'dark' : 'light') : null;

interface ViewTransitionDocument {
  startViewTransition?: (update: () => void) => { finished: Promise<void> };
}

/**
 * Changes the colours of the whole page at once. Where the browser can, the
 * old page fades into the new one (a view transition); elsewhere every colour
 * glides together for the same moment. Either way nothing changes on its own
 * clock, so the page does not twitch. Reduced motion: at once.
 */
function changeSmoothly(update: () => void): void {
  const root = document.documentElement;
  const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || document.hidden) {
    update();
    return;
  }
  const doc = document as Document & ViewTransitionDocument;
  if (typeof doc.startViewTransition === 'function') {
    root.classList.add('theme-fade');
    doc
      .startViewTransition(update)
      .finished.catch(() => undefined)
      .finally(() => {
        root.classList.remove('theme-fade');
      });
    return;
  }
  root.classList.add('theme-glide');
  update();
  window.setTimeout(() => {
    root.classList.remove('theme-glide');
  }, 400);
}

function apply(choice: ThemeChoice): void {
  const root = document.documentElement;
  const system = query()?.matches === true ? 'dark' : 'light';
  const before = resolve(root.dataset.theme, root.dataset.system);
  const after = resolve(choice, system);
  const update = (): void => {
    root.dataset.theme = choice;
    root.dataset.system = system;
  };
  // the first paint and a choice that keeps the same colours change nothing visible
  if (before === null || before === after) update();
  else changeSmoothly(update);
}

/** Theme follows the system by default; the footer switch sets System, Light or Dark. */
export function useTheme(): { choice: ThemeChoice; resolved: 'light' | 'dark'; set: (next: ThemeChoice) => void } {
  const [choice, setChoice] = useState<ThemeChoice>(() => getThemeChoice());
  const [system, setSystem] = useState<Resolved>(() => (query()?.matches === true ? 'dark' : 'light'));

  useEffect(() => {
    apply(choice);
    const mq = query();
    if (!mq) return undefined;
    const onChange = (event: MediaQueryListEvent): void => {
      setSystem(event.matches ? 'dark' : 'light');
      apply(choice);
    };
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, [choice]);

  const set = useCallback((next: ThemeChoice) => {
    setThemeChoice(next);
    setChoice(next);
  }, []);

  const resolved = choice === 'auto' ? system : choice;
  return { choice, resolved, set };
}
